import React from "react";
import PropTypes from "prop-types";

import {DateFormatter} from "../../../../services/date";
import BookingService from "../../../../services/bookings";
import CountryFlag from "../../../../components/CountryFlag";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {differenceInYears} from "date-fns";

function calculateAge(dob) {
   return differenceInYears(new Date(), dob);
}


const GuestItem = (props) => {
    const {
        guest,
        showStatus,
        showTouristTax=false,
        disableVerticalPadding,
        disableHorizontalPadding,
    } = props;

    const residency = guest.placeOfResidence
        ? `${guest.placeOfResidence.name} (${guest.placeOfResidence.districtInitials})`
        : guest.nationality.label;

    const dateOfBirth = DateFormatter.longString(guest.dateOfBirth);
    const placeOfBirth = guest.placeOfBirth
        ? `${guest.placeOfBirth.name} (${guest.placeOfBirth.districtInitials})`
        : guest.countryOfBirth.label;

    const documentType = BookingService.enums.DOCUMENTS_TYPES.find(d => d.value === guest.documentType);
    const documentTypeName = documentType ? documentType.label : "--";

    const verticalPadding = disableVerticalPadding ? 0 : 1;
    const horizontalPadding = disableHorizontalPadding ? 0 : 1;

    return (
        <Grid container sx={{py: verticalPadding, px: horizontalPadding}} alignItems="center" rowSpacing={0.5}>
            <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>
                        {guest.getFullName()}
                    </Typography>
                    {
                        guest.dateOfBirth &&
                        <Typography variant="body2" fontWeight='light'>
                            {`${calculateAge(guest.dateOfBirth)} anni`}
                        </Typography>
                    }
                    <Typography fontWeight='light' sx={{ml: 1, fontSize: "0.75rem", fontWeight: 200}}>
                        ({guest.sex})
                    </Typography>
                </Stack>
                <Typography variant="body2" fontWeight='light' sx={{'& > img': {mr: 1}}}>
                    <CountryFlag code={guest.nationality.code}/>
                    {residency}
                </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight='light'>
                    {`Nato il ${dateOfBirth} a ${placeOfBirth}`}
                </Typography>
                {
                    guest.hasIdDocument() &&
                    <Typography variant="body2" fontWeight='light'>
                        {`${documentTypeName}: ${guest.documentNumber}`}
                    </Typography>
                }
                {
                    showTouristTax && <Box sx={{mt: 0.5, display: "flex", alignItems: "center"}}>
                        {
                            guest.hasLocalTouristTaxExclusion() ?
                                <Typography variant="body2" color="secondary" fontWeight='light' sx={{mr: 1}}>
                                    Esente dalla tassa di soggiorno
                                </Typography>
                                :
                                <Typography variant="body2" color="secondary" fontWeight='light' sx={{mr: 1}}>
                                    Tassa di sogg. applicabile
                                </Typography>
                        }
                    </Box>
                }
            </Grid>

            {
                showStatus && <>{guest.guestStays.map(gs => <GuestStayGridRow key={gs.id} guestStay={gs} />)}</>
            }

        </Grid>
    );
}

const GuestStayGridRow = ({guestStay}) => {
    const arrival = DateFormatter.shortString(guestStay.arrival);
    const departure = guestStay.departure ? DateFormatter.shortString(guestStay.departure) : null;

    // Alloggiati chip color and icon
    let alloggiatiColor = "disabled";
    let alloggiatiIcon = <CloseIcon/>;
    if(guestStay.alloggiatiStay?.alloggiatiUploadedAt) {
        alloggiatiColor = "success";
        alloggiatiIcon = <CheckIcon/>;
    }

    // Abit chip color and icon
    let abitArrivalColor = "disabled";
    let abitArrivalIcon = <CloseIcon/>;
    if (guestStay.abitStay?.abitArrival) {
        if(guestStay.abitStay.abitArrivalUploadedAt) {
            abitArrivalColor = "success";
            abitArrivalIcon = <CheckIcon/>;
        } else if (guestStay.abitStay.abitArrivalError) {
            abitArrivalColor = "error";
        } else{
            abitArrivalColor = "info";
            abitArrivalIcon = <AccessTimeIcon/>;
        }
    }

    // Abit departure chip color and icon
    let abitDepartureColor = "disabled";
    let abitDepartureIcon = <CloseIcon/>;
    if (guestStay.abitStay?.abitDeparture) {
        if(guestStay.abitStay.abitDepartureUploadedAt) {
            abitDepartureColor = "success";
            abitDepartureIcon = <CheckIcon/>;
        } else if (guestStay.abitStay.abitDepartureError) {
            abitDepartureColor = "error";
        } else{
            abitDepartureColor = "info";
            abitDepartureIcon = <AccessTimeIcon/>;
        }
    }

    return <>
        <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight='light' sx={{mr: 1}}>
                Soggiorno: {arrival} - {departure ?? 'in corso'}
            </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
            <Stack direction="row" flexWrap="wrap" spacing={1} alignItems="center">
                <Chip label="Sched.Allog."
                      size="small"
                      variant="outlined"
                      color={alloggiatiColor}
                      icon={alloggiatiIcon}/>
                <Chip label="Abit Arr."
                      size="small"
                      variant="outlined"
                      color={abitArrivalColor}
                      icon={abitArrivalIcon}/>
                <Chip label="Abit Part."
                      size="small"
                      variant="outlined"
                      color={abitDepartureColor}
                      icon={abitDepartureIcon}/>
            </Stack>
        </Grid>
    </>
}

GuestItem.propTypes = {
    guest: PropTypes.object.isRequired,
    showStatus: PropTypes.bool.isRequired,
    disableVerticalPadding: PropTypes.bool,
    disableHorizontalPadding: PropTypes.bool,
    showTouristTax: PropTypes.bool,
}

export default GuestItem;